import { graphQlClient, graphQlMultipleUpload } from '@config/graphqlClient';
import ProjectPlanDraftGraphQL from '@graphql/projectPlanDraft.queries';
import {
  ProjectPlanDraft,
  CreateProjectPlanDraftInput,
  ReviseProjectPlanDraftInput,
  UpdateProjectPlanDraftPlanInput,
  ImportProjectPlanDraftInput,
  SaveProjectPlanDraftAsTemplateInput,
  AiPlanningAvailableRequest,
  ProjectPlanDraftRequest,
  MyProjectPlanDraftsRequest,
} from '@models/projectPlanDraft.model';

export const getAiPlanningAvailable = async (): Promise<boolean> => {
  const data: AiPlanningAvailableRequest = await graphQlClient.request(
    ProjectPlanDraftGraphQL.queries.aiPlanningAvailable,
  );
  return !!data.aiPlanningAvailable;
};

export const getProjectPlanDraft = async (draftId: string): Promise<ProjectPlanDraft> => {
  const data: ProjectPlanDraftRequest = await graphQlClient.request(
    ProjectPlanDraftGraphQL.queries.projectPlanDraft,
    { draftId },
  );
  return data.projectPlanDraft;
};

export const getMyProjectPlanDrafts = async (): Promise<ProjectPlanDraft[]> => {
  const data: MyProjectPlanDraftsRequest = await graphQlClient.request(
    ProjectPlanDraftGraphQL.queries.myProjectPlanDrafts,
  );
  return data.myProjectPlanDrafts || [];
};

export const createProjectPlanDraft = async (
  input: CreateProjectPlanDraftInput,
): Promise<ProjectPlanDraft> => {
  const { files, ...rest } = input;
  if (files && files.length > 0) {
    const data = await graphQlMultipleUpload(
      files,
      'input',
      ProjectPlanDraftGraphQL.mutations.createProjectPlanDraft,
      rest,
    );
    if (!data?.createProjectPlanDraft) {
      // graphQlMultipleUpload reports the GraphQL errors itself but returns null data instead of
      // throwing. The `response` shape makes getErrorMessage use the translated fallback.
      throw Object.assign(new Error('createProjectPlanDraft upload failed'), { response: {} });
    }
    return data.createProjectPlanDraft;
  }
  const data: { createProjectPlanDraft: ProjectPlanDraft } = await graphQlClient.request(
    ProjectPlanDraftGraphQL.mutations.createProjectPlanDraft,
    { input: rest },
  );
  return data.createProjectPlanDraft;
};

export const reviseProjectPlanDraft = async (
  input: ReviseProjectPlanDraftInput,
): Promise<ProjectPlanDraft> => {
  const data: { reviseProjectPlanDraft: ProjectPlanDraft } = await graphQlClient.request(
    ProjectPlanDraftGraphQL.mutations.reviseProjectPlanDraft,
    { input },
  );
  return data.reviseProjectPlanDraft;
};

export const retryProjectPlanDraft = async (draftId: string): Promise<ProjectPlanDraft> => {
  const data: { retryProjectPlanDraft: ProjectPlanDraft } = await graphQlClient.request(
    ProjectPlanDraftGraphQL.mutations.retryProjectPlanDraft,
    { input: { draftId } },
  );
  return data.retryProjectPlanDraft;
};

export const updateProjectPlanDraftPlan = async (
  input: UpdateProjectPlanDraftPlanInput,
): Promise<ProjectPlanDraft> => {
  const data: { updateProjectPlanDraftPlan: ProjectPlanDraft } = await graphQlClient.request(
    ProjectPlanDraftGraphQL.mutations.updateProjectPlanDraftPlan,
    { input },
  );
  return data.updateProjectPlanDraftPlan;
};

export const importProjectPlanDraft = async (
  input: ImportProjectPlanDraftInput,
): Promise<{ taskId: string; name: string }[]> => {
  const data: { importProjectPlanDraft: { taskId: string; name: string }[] } = await graphQlClient.request(
    ProjectPlanDraftGraphQL.mutations.importProjectPlanDraft,
    { input },
  );
  return data.importProjectPlanDraft;
};

export const saveProjectPlanDraftAsTemplate = async (
  input: SaveProjectPlanDraftAsTemplateInput,
): Promise<boolean> => {
  const data: { saveProjectPlanDraftAsTemplate: boolean } = await graphQlClient.request(
    ProjectPlanDraftGraphQL.mutations.saveProjectPlanDraftAsTemplate,
    { input },
  );
  return data.saveProjectPlanDraftAsTemplate;
};

export const discardProjectPlanDraft = async (draftId: string): Promise<boolean> => {
  const data: { discardProjectPlanDraft: boolean } = await graphQlClient.request(
    ProjectPlanDraftGraphQL.mutations.discardProjectPlanDraft,
    { input: { draftId } },
  );
  return data.discardProjectPlanDraft;
};

const projectPlanDraftService = {
  getAiPlanningAvailable,
  getProjectPlanDraft,
  getMyProjectPlanDrafts,
  createProjectPlanDraft,
  reviseProjectPlanDraft,
  retryProjectPlanDraft,
  updateProjectPlanDraftPlan,
  importProjectPlanDraft,
  saveProjectPlanDraftAsTemplate,
  discardProjectPlanDraft,
};

export default projectPlanDraftService;
